import { useMemo } from "react";

import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { type Dayjs } from "dayjs";
import { apiGetLocationCharts, apiGetLocationDashboard, apiGetLocationDashboardTab } from "src/api/locationCharting.api";
import {
  GetLocationDashboardQueryKey,
  GetLocationDashboardTabQueryKey,
} from "src/components/LocationBreadcrumbs/helpers/breadcrumbs.helper";
import type Granularity from "src/constants/granularity.enum";
import IntervalType from "src/constants/intervalType.enum";
import MeasureType from "src/constants/measureType.enum";
import { getIntervalQueryDate, getToday } from "src/lib/date.helper";
import { getDateOnlyPollingSettings, getIntervalPollingSettings } from "src/lib/pollingSettings.helper";
import { type CountItemsDTO } from "src/types/countItemDto";
import { type MeasuresItemizedCompactDto, type MeasuresSummarizedDto } from "src/types/dashboardDatasetDto";
import { EventItemsDTO } from "src/types/eventItemDto";
import { LocationModel } from "src/types/location";
import { type MeasuresItemizedDto } from "src/types/locationChartDto";
import { type WeatherItemsDTO } from "src/types/weatherItemDto";

import { apiGetCountData, apiGetEventForecastData, apiGetWeatherForecastData } from "./location.api";

export const useWeatherForecastQuery = (locationId: string, date: Dayjs | null): UseQueryResult<WeatherItemsDTO, Error> => {
  const filter = useMemo(() => `MeasureTime eq ${date?.format("YYYY-MM-DD")}`, [date]);

  return useQuery<WeatherItemsDTO, Error>({
    queryKey: ["weatherQuery", { locationId, filter }],
    queryFn: () => apiGetWeatherForecastData(locationId, filter),
    enabled: !!locationId,
    ...getDateOnlyPollingSettings(date),
  });
};

export const useEventForecastQuery = (location: LocationModel, date: Dayjs | null): UseQueryResult<EventItemsDTO, Error> => {
  const filter = useMemo(() => {
    const dateFilter = date ? `MeasureTime ge ${date.format("YYYY-MM-DD")}` : "";

    const ids = (location.PathId?.split("/") ?? []).filter(Boolean).reverse();

    const locationFilter = ids.length ? `(${ids.map((id) => `LocationId eq ${id}`).join(" or ")})` : "";

    return [dateFilter, locationFilter].filter(Boolean).join(" and ");
  }, [date, location.PathId]);

  return useQuery<EventItemsDTO, Error>({
    queryKey: ["eventQuery", { locationId: location.Id, filter }],
    queryFn: () => apiGetEventForecastData(location.Id, filter),
    enabled: !!location.Id,
    ...getDateOnlyPollingSettings(date),
  });
};

export const useCountLastHourQuery = (
  locationId: string,
  granularity: Granularity,
  date: Dayjs | null
): UseQueryResult<CountItemsDTO, Error> => {
  const selectedDate = date ?? getToday();

  const queryString = useMemo(() => {
    const now = date ?? getToday();
    const startDate = now.add(-1, "hour");
    return `Granularity eq '${granularity}' and MeasureTime ge ${startDate.format("YYYY-MM-DDTHH:mm:ssZ")} and MeasureTime lt ${now.format("YYYY-MM-DDTHH:mm:ssZ")}`;
  }, [date, granularity]);

  return useQuery<CountItemsDTO, Error>({
    queryKey: ["countLastHourQuery", { locationId, granularity, date: date?.toISOString() ?? null }],
    queryFn: () => apiGetCountData(locationId, queryString),
    enabled: !!locationId,
    ...getIntervalPollingSettings(IntervalType.Hour, selectedDate),
  });
};

export const useHistoryChartQuery = (
  locationId: string,
  intervalType: IntervalType,
  date: Dayjs | null,
  measureType: MeasureType,
  lastDays: number | null = null,
  isWeather: boolean | null = false,
  isEvents: boolean | null = false
): UseQueryResult<MeasuresItemizedDto, Error> => {
  const queryDate = getIntervalQueryDate(intervalType, date);
  const queryKey = [`locationCharts`, { intervalType, locationId, date: queryDate, seriesType: measureType, lastDays, isWeather, isEvents }];
  return useQuery<MeasuresItemizedDto, Error>({
    queryKey,
    queryFn: async () => apiGetLocationCharts(locationId, intervalType, queryDate, measureType, lastDays, isWeather, isEvents),
    ...getIntervalPollingSettings(intervalType, queryDate),
  });
};

export const useSummarizedHistoryChartQuery = (
  locationIds: string[],
  intervalType: IntervalType,
  date: Dayjs | null,
  measureType: MeasureType,
  lastDays: number | null = null,
  granularity: Granularity | null = null,
  pinned = false,
  fetchParentData = false
): UseQueryResult<MeasuresSummarizedDto, Error> => {
  const queryDate = getIntervalQueryDate(intervalType, date);
  const queryKey = GetLocationDashboardQueryKey(locationIds, intervalType, queryDate, measureType, false, 0, lastDays, granularity, pinned, fetchParentData);
  return useQuery<MeasuresSummarizedDto, Error>({
    queryKey,
    queryFn: async () =>
      apiGetLocationDashboard(locationIds, intervalType, queryDate, measureType, false, 0, lastDays, granularity, pinned, fetchParentData),
    ...getIntervalPollingSettings(intervalType, queryDate),
  });
};

export const useHistoryIemizedCompactChartQuery = (
  locationIds: string[],
  intervalType: IntervalType,
  date: Dayjs | null,
  lastDays: number | null = null,
  pinned = false
): UseQueryResult<MeasuresItemizedCompactDto, Error> => {
  const queryDate = getIntervalQueryDate(intervalType, date);
  const queryKey = GetLocationDashboardTabQueryKey(locationIds, intervalType, queryDate, MeasureType.Count, lastDays, null, pinned);
  return useQuery<MeasuresItemizedCompactDto, Error>({
    queryKey,
    queryFn: async () => apiGetLocationDashboardTab(locationIds, intervalType, queryDate, MeasureType.Count, lastDays, pinned),
    ...getIntervalPollingSettings(intervalType, queryDate),
  });
};
